import i18n from '@risksmart-app/i18n/src/i18n';
import { z } from 'zod';

import type { CycleCheckContext } from './conditionalLogicGraph';
import { findCycleVertices } from './conditionalLogicGraph';

const conditionalLogicRulesSchema = z
  .array(
    z.object({
      ifField: z.string(),
      values: z.array(z.string()),
      // True for a code-declared read-only default rule surfaced in merge mode.
      // Locked rows render non-editable and are stripped before persisting;
      // only customer (unlocked) rules are saved.
      locked: z.boolean().optional(),
    })
  )
  .optional()
  .default([]);

const optionSchema = z.object({
  id: z.string(),
  label: z.string().min(1, 'Option label is required'),
});

export const OPTION_FIELD_TYPES = ['select', 'multiselect'] as const;

export const isOptionFieldType = (type: string): boolean =>
  (OPTION_FIELD_TYPES as readonly string[]).includes(type);

export const DEFAULT_VALUE_FIELD_TYPES = ['text', 'textarea', 'link'] as const;

export const supportsDefaultValue = (type: string): boolean =>
  (DEFAULT_VALUE_FIELD_TYPES as readonly string[]).includes(type);

const rulesValidationRefine = (
  data: {
    conditionalLogicEnabled: boolean;
    conditionalLogicRules?: { ifField: string; values: string[] }[];
  },
  zctx: z.RefinementCtx
) => {
  if (!data.conditionalLogicEnabled || !data.conditionalLogicRules?.length) {
    return;
  }
  data.conditionalLogicRules.forEach((rule, index) => {
    if (!rule.ifField) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Field is required',
        path: ['conditionalLogicRules', index, 'ifField'],
      });
    }
    if (!rule.values || rule.values.length === 0) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one value is required',
        path: ['conditionalLogicRules', index, 'values'],
      });
    }
  });
};

/**
 * Merge mode rejects a customer (unlocked) rule that targets the same `ifField`
 * as a locked default rule: AND-ing two rules on one field can only narrow (and
 * with disjoint values becomes unsatisfiable), so the field would silently never
 * show. The picker already hides fields used by another rule, so this is a
 * save-time backstop.
 */
const lockedConflictRefine = (
  data: {
    conditionalLogicEnabled: boolean;
    conditionalLogicRules?: { ifField: string; locked?: boolean }[];
  },
  zctx: z.RefinementCtx
) => {
  const rules = data.conditionalLogicRules;
  if (!data.conditionalLogicEnabled || !rules?.length) {
    return;
  }
  const lockedIfFields = new Set(
    rules.filter((r) => r.locked && r.ifField).map((r) => r.ifField)
  );
  if (lockedIfFields.size === 0) {
    return;
  }
  rules.forEach((rule, index) => {
    if (!rule.locked && rule.ifField && lockedIfFields.has(rule.ifField)) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('formBuilder.formField.lockedConflictErrorMessage'),
        path: ['conditionalLogicRules', index, 'ifField'],
      });
    }
  });
};

export const sectionEditorSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    conditionalLogicEnabled: z.boolean().default(false),
    conditionalLogicRules: conditionalLogicRulesSchema,
    guidanceEnabled: z.boolean().default(false),
    guidance: z.string().optional(),
  })
  .superRefine(rulesValidationRefine);

const fieldEditorObjectSchema = z.object({
  fieldType: z.string().min(1, 'Field type is required'),
  fieldName: z.string().min(1, 'Field name is required'),
  required: z.boolean().optional().default(false),
  readOnly: z.boolean().optional().default(false),
  hidden: z.boolean().optional().default(false),
  defaultValueEnabled: z.boolean().optional().default(false),
  defaultValue: z.string().optional().default(''),
  options: z.array(optionSchema).optional().default([]),
  conditionalLogicEnabled: z.boolean().default(false),
  conditionalLogicRules: conditionalLogicRulesSchema,
  // Provenance of a code-declared default condition. `locked` → read-only
  // default: rendered non-editable and never persisted. `isDefault` →
  // unmodified editable default: not persisted until the admin edits it.
  conditionalLogicLocked: z.boolean().optional(),
  conditionalLogicIsDefault: z.boolean().optional(),
  guidanceEnabled: z.boolean().default(false),
  guidance: z.string().optional(),
});

/**
 * Option-list validation (count + label uniqueness) for `select`/`multiselect`
 * field types. Only meaningful when the admin actually edits the option list —
 * a standard field that maps to one of these types (e.g. the Tags field →
 * `multiselect`) has system-managed values and no editor option list, so this
 * refine is skipped for it (see {@link createFieldEditorSchema}). Otherwise the
 * unsatisfiable "At least 2 options" error would silently block saving.
 */
const optionsValidationRefine = (
  data: { fieldType: string; options: { id: string; label: string }[] },
  ctx: z.RefinementCtx
) => {
  if (!isOptionFieldType(data.fieldType)) {
    return;
  }
  if (data.options.length < 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least 2 options are required',
      path: ['options'],
    });
  }

  const labels = data.options.map((o) => o.label.trim().toLowerCase());
  const seen = new Set<string>();
  labels.forEach((label, index) => {
    if (seen.has(label)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Option labels must be unique',
        path: ['options', index, 'label'],
      });
    }
    seen.add(label);
  });
};

const defaultValueValidationRefine = (
  data: {
    fieldType: string;
    defaultValueEnabled: boolean;
    defaultValue: string;
  },
  ctx: z.RefinementCtx
) => {
  if (
    data.defaultValueEnabled &&
    (!data.defaultValue || data.defaultValue.trim().length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Default value is required',
      path: ['defaultValue'],
    });
  }

  if (
    data.fieldType === 'url' &&
    data.defaultValue &&
    data.defaultValue.length > 0
  ) {
    try {
      const url = new URL(data.defaultValue);
      Boolean(url);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid URL',
        path: ['defaultValue'],
      });
    }
  }
};

export const fieldEditorSchema = fieldEditorObjectSchema
  .superRefine(rulesValidationRefine)
  .superRefine(lockedConflictRefine)
  .superRefine(optionsValidationRefine)
  .superRefine(defaultValueValidationRefine);

export type SectionEditorValues = z.infer<typeof sectionEditorSchema>;
export type FieldEditorValues = z.infer<typeof fieldEditorSchema>;

const CYCLE_ERROR_MESSAGE =
  'One or more rules create a circular dependency. Remove or change the rule(s) that cause this.';

const SELF_REFERENCE_MESSAGE = 'A field cannot depend on itself';

const SECTION_OWN_FIELD_MESSAGE =
  'A section cannot depend on a field inside the same section';

const formatCyclePathMessage = (
  cycleVertices: string[],
  getEntityLabel?: (vertexId: string) => string
): string => {
  const labels = cycleVertices.map(
    (v) => getEntityLabel?.(v) ?? v.replace(/^(field|section):/, '')
  );

  return `${CYCLE_ERROR_MESSAGE} Cycle: ${labels.join(' → ')}.`;
};

const cycleCheckRefine =
  (entityType: 'field' | 'section', ctx: CycleCheckContext | undefined) =>
  (
    data: {
      conditionalLogicEnabled: boolean;
      conditionalLogicRules?: { ifField: string }[];
    },
    zctx: z.RefinementCtx
  ) => {
    // Run the check whenever rules exist; a disabled toggle still produces a
    // live cycle the moment it's re-enabled.
    if (!ctx || !data.conditionalLogicRules?.length) {
      return;
    }

    if (entityType === 'field') {
      data.conditionalLogicRules.forEach((rule, idx) => {
        if (rule.ifField && rule.ifField === ctx.entityId) {
          zctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SELF_REFERENCE_MESSAGE,
            path: ['conditionalLogicRules', idx, 'ifField'],
          });
        }
      });
    }

    if (entityType === 'section') {
      const fieldsInSection = new Set(ctx.containers[ctx.entityId] ?? []);
      data.conditionalLogicRules.forEach((rule, idx) => {
        if (rule.ifField && fieldsInSection.has(rule.ifField)) {
          zctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SECTION_OWN_FIELD_MESSAGE,
            path: ['conditionalLogicRules', idx, 'ifField'],
          });
        }
      });
    }

    const cycleVertices = findCycleVertices({
      ...ctx,
      entityType,
      proposedRules: data.conditionalLogicRules,
    });
    if (cycleVertices) {
      zctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: formatCyclePathMessage(cycleVertices, ctx.getEntityLabel),
        path: ['conditionalLogicRules'],
      });
    }
  };

export const createSectionEditorSchema = (ctx?: CycleCheckContext) =>
  sectionEditorSchema.superRefine(cycleCheckRefine('section', ctx));

/**
 * Builds the field-editor schema with cycle detection.
 *
 * `optionsEditable` mirrors the dialog's OptionsEditor visibility: standard
 * fields lock the field type and hide the option list (their values are
 * system-managed — e.g. the Tags field's tag types), so the `select`/
 * `multiselect` option-count requirement must NOT apply to them. Leaving it on
 * makes the "At least 2 options" error unsatisfiable and silently blocks the
 * save button. Custom fields (default) keep full option validation.
 */
export const createFieldEditorSchema = (
  ctx?: CycleCheckContext,
  optionsEditable = true
) => {
  const base = optionsEditable
    ? fieldEditorSchema
    : fieldEditorObjectSchema
        .superRefine(rulesValidationRefine)
        .superRefine(lockedConflictRefine)
        .superRefine(defaultValueValidationRefine);

  return base.superRefine(cycleCheckRefine('field', ctx));
};
